import React, { forwardRef, PropsWithChildren, ReactNode, Ref } from 'react';
import styled from 'styled-components';

const SlotContainer = styled.div<{
  $mergedCell?: boolean;
  $invalid?: boolean;
  $wrap?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 12px 11px 21px;
  grid-column: ${p => (p.$mergedCell ? '1 / -1' : 'auto')};
  box-sizing: border-box;
  background: #ffffff;
  border: 0.864023px solid ${p => (p.$invalid ? 'red' : '#e7e7e7')};
  box-shadow: 0px 0.8px 7px rgba(103, 103, 103, 0.03);
  border-radius: 8px;

  ${p =>
    p.$wrap &&
    `
    @supports (container-type: inline-size) {
      @container(width < 394px) {
        flex-wrap: wrap;
        row-gap: 12px;
      }
    }
    @supports not (container-type: inline-size) {
      @media(max-width: < 394px) {
        flex-wrap: wrap;
        row-gap: 12px;
      }
    }
    `}
`;

export const SlotLabel = styled.span`
  flex-shrink: 0;
  min-width: 120px;
  box-sizing: border-box;
  padding-right: 12px;

  & > * {
    vertical-align: middle;
  }
`;

export const SlotLabelText = styled.span`
  white-space: nowrap;
  margin-left: 18px;
  font-size: 14px;
`;

export interface SlotWrapperProps {
  mergedCell?: boolean;
  label?: string;
  icon?: ReactNode;
  invalid?: boolean;
  wrap?: boolean;
}
export const SlotWrapper = forwardRef(
  (
    {
      mergedCell,
      label,
      icon,
      invalid,
      wrap,
      children,
    }: SlotWrapperProps & PropsWithChildren,
    ref: Ref<HTMLDivElement>,
  ) => {
    return (
      <SlotContainer
        ref={ref}
        $mergedCell={mergedCell}
        $invalid={invalid}
        $wrap={wrap}>
        <SlotLabel>
          {icon}
          <SlotLabelText>{label}</SlotLabelText>
        </SlotLabel>
        {children}
      </SlotContainer>
    );
  },
);

const MultiRowSlotContainer = styled.div<{
  $mergedCell?: boolean;
  $invalid?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 11px 25px 11px 21px;
  gap: 11px;
  grid-column: ${p => (p.$mergedCell ? '1 / -1' : 'auto')};
  box-sizing: border-box;
  background: #ffffff;
  border: 0.864023px solid ${p => (p.$invalid ? 'red' : '#e7e7e7')};
  box-shadow: 0px 0.8px 7px rgba(103, 103, 103, 0.03);
  border-radius: 8px;

  & > * {
    flex: 1 1 auto;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;

  & > *:first-child {
    margin-right: -12px;
  }
`;

export interface MultiRowSlotWrapperProps extends SlotWrapperProps {
  children?: ReactNode[];
  hideLabelPlaceholder?: boolean;
}
export const MultiRowSlotWrapper = forwardRef(
  (
    {
      mergedCell,
      label,
      icon,
      children,
      invalid,
      hideLabelPlaceholder = false,
    }: MultiRowSlotWrapperProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    return (
      <MultiRowSlotContainer
        ref={ref}
        $mergedCell={mergedCell}
        $invalid={invalid}>
        {children?.map(
          (node, idx) =>
            node !== null && (
              <Row key={idx}>
                {idx === 0 ? (
                  <SlotLabel>
                    {icon}
                    <SlotLabelText>{label}</SlotLabelText>
                  </SlotLabel>
                ) : (
                  !hideLabelPlaceholder && <SlotLabel />
                )}
                {node}
              </Row>
            ),
        )}
      </MultiRowSlotContainer>
    );
  },
);

export const SlotGridWrapper = styled.div`
  width: 100%;
  container-type: inline-size;
`;

export const SlotGrid = styled.div<{
  $marginTop?: string;
  $width: number;
  $columnGap: number;
}>`
  display: grid;
  grid-template-columns: ${p => p.$width}px ${p => p.$width}px;
  column-gap: ${p => p.$columnGap}px;
  margin-top: ${p => p.$marginTop ?? 'auto'};
  margin-right: auto;
  margin-left: auto;
  width: fit-content;
  max-width: 100%;
  overflow: auto;

  // SlotGrid 가 parent 보다 커서 overflow 가 일어날 때 2열에서 1열로 만들어줌
  @container (width < ${p => p.$width * 2 + p.$columnGap}px) {
    grid-template-columns: 100% !important; // 1fr은 넘쳐흘러서 100% 로 방지
    min-width: min(${p => p.$width}px, 100%);
    justify-content: center;

    & > br {
      display: none;
    }
  }

  @supports not (container-type: inline-size) {
    @media (max-width: 649px) {
      grid-template-columns: 100% !important; // 1fr은 넘쳐흘러서 100% 로 방지
      min-width: min(${p => p.$width}px, 100%);
      justify-content: center;

      & > br {
        display: none;
      }
    }
  }
`;
